import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {FC, useState, useEffect, useMemo} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Spinner, Button, Form, Dropdown, ButtonGroup} from 'react-bootstrap'
import {showSwal} from '../../../../ui-kit'
import {
  createHSCategory,
  createHSCode,
  deleteHSCode,
  exportTemplate,
  getHSCategoryByID,
  getHSOptions,
  updateHSCategoryById,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {HSCategory, HSCategoryItems} from './core/_models'
import {Link, useNavigate, useParams} from 'react-router-dom'
import './MasterDataHSCategoryDetailPage.scss'
import DataTable from '../../../../ui-kit/DataTable'
import ModalAddHSCode from './components/ModalAddHSCode'
import {importDataMapping} from '../master-data-hs-category/core/_requests'
import ModalImportHSCategoriesMapping from '../master-data-hs-category/components/ModalImportHSCategoriesMapping'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'



interface DataRow {
  id: string
  partial?: boolean | null
  category_id?: string
  hs: string
  hs_description: string
  hs_version: string
  total: number
}

const columnHelper = createColumnHelper<DataRow>()

const extractImgSrc = (svgString) => {
  const match = svgString.match(/xlink:href="([^"]+)"/)
  return match ? match[1] : null
}

const MasterDataHSCategoryDetailPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const {idHS} = useParams()
  const isDetailMode = Boolean(idHS)
  const isAddMode = !idHS

  const [parentCategory, setParentCategory] = useState([])
  const [hsType, setHSType] = useState([])
  const [hsCategoryDetail, setHSCategoryDetail] = useState<HSCategory>({
    id: idHS ?? '',
    name: '',
    name_fr: '',
    code: null,
    category_type: null,
    parent_id: null,
    parent_category: null,
    desc: null,
    partial_icon_svg: null,
    full_icon_svg: null,
    in_force_tariff: null,
    in_force_tariff_type: null,
    items: [],
  })
  const [dataTable, setDataTable] = useState<DataRow[]>([])
  const [includeZero, setIncludeZero] = useState(false)

  const fetchInitData = async () => {
    try {
      setIsLoadingData(true)

      const optionsRes = await getHSOptions(axiosPrivate)
      const resDataOpt = optionsRes.data.data
      setHSType(resDataOpt.type)
      setParentCategory(resDataOpt.parent_category)

      if (isDetailMode) {
        const detailRes = await getHSCategoryByID(axiosPrivate, {idHS})
        if (detailRes) {
          const resData = detailRes.data.data
          setHSCategoryDetail((prev) => ({
            ...prev,
            name: resData.name,
            name_fr: resData.name_fr,
            code: resData.code,
            category_type: resData.category_type,
            parent_id: resData.parent_id,
            parent_category: resData.parent_category,
            desc: resData.desc,
            partial_icon_svg: resData.partial_icon_svg,
            full_icon_svg: resData.full_icon_svg,
            in_force_tariff: resData.in_force_tariff,
            in_force_tariff_type: resData.in_force_tariff_type,
            items: resData.items,
          }))

          setDataTable(resData.items)
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingData(false)
    }
  }

  const columns: ColumnDef<DataRow, any>[] = useMemo(
    () => [
      columnHelper.accessor('hs', {
        header: 'Code',
        cell: (info) => <span className='badge text-white bg-dark'>{info.getValue()}</span>,
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left font-semibold text-dark',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('hs_version', {
        header: 'Version',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left font-semibold text-dark',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('hs_description', {
        header: 'Description',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left font-semibold text-dark',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('partial', {
        header: 'Partial',
        cell: (info) => (
          <span className={`partial-value ${info.getValue() ? 'partial-true' : 'partial-false'}`}>
            {info.getValue() ? 'true' : 'false'}
          </span>
        ),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('id', {
        header: 'Actions',
        cell: (info) => {
          return ability.can('delete', subject) ? (
            <Link
              to='#'
              className='link-danger'
              onClick={() => handleListHsDeletion(info.getValue())}
            >
              <span className='fa fa-trash'></span>
            </Link>
          ) : (
            ''
          )
        },
        meta: {
          headerClass: 'text-center',
          cellClass: 'text-center',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [includeZero]
  ) // Columns don't depend on state, so empty dependency array is fine here

  useEffect(() => {
    fetchInitData()
  }, [])

  const handleListHsDeletion = (id) => {
    showSwal({
      title: 'Are you sure you want to delete this HS Code?',
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)

      deleteHSCode(axiosPrivate, id)
        .then((res) => {
          if (res.status === 200) {
            showSwal('HS Code deleted', undefined, 'success').then(() => {
              window.location.reload()
            })
          }
        })
        .catch((error) => {
          console.error(error)
          showSwal('Error', 'Failed to delete HS Code', 'error')
        })
        .finally(() => {
          setIsLoadingButton(false)
        })
    })
  }

  // Update detail HS Category
  const handleDataChange = (e) => {
    const {name, value} = e.target
    const file = (e.target as HTMLInputElement).files?.[0]

    if (file) {
      const reader = new FileReader()

      // Update state if file is read
      reader.onload = (event) => {
        const svgContent = event.target?.result as string
        setHSCategoryDetail((prev) => ({
          ...prev,
          [name]: svgContent,
        }))
      }

      reader.readAsText(file)
    }

    setHSCategoryDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = () => {
    const body = {
      name: hsCategoryDetail.name,
      name_fr: hsCategoryDetail.name_fr,
      code: hsCategoryDetail.code,
      parent_id: hsCategoryDetail.parent_id,
      category_type: hsCategoryDetail.category_type,
      partial_icon_svg: hsCategoryDetail.partial_icon_svg,
      full_icon_svg: hsCategoryDetail.full_icon_svg,
      desc: hsCategoryDetail.desc,
      in_force_tariff: hsCategoryDetail.in_force_tariff,
      in_force_tariff_type: hsCategoryDetail.in_force_tariff_type,
    }

    showSwal({
      title: `Are you sure you want to ${isEditMode ? 'update' : 'create'} this HS Category?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)

      if (isEditMode) {
        updateHSCategoryById(axiosPrivate, hsCategoryDetail.id, body)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to update HS Category', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        createHSCategory(axiosPrivate, body)
          .then((res) => {
            if (res.status === 201) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/master-data-hs-categories')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to create HS Category', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isEditMode) {
          window.location.reload()
        } else {
          navigate('/master-data-hs-categories')
        }
      }
    })
  }
  // end of Update detail HS Category

  // Add HS Category Item
  const [showAddModal, setShowAddModal] = useState(false)
  const [isLoadingModal, setIsLoadingModal] = useState(false)
  const [errorModal, setErrorModal] = useState<string | null>(null)
  const [hsItemData, setHSItemData] = useState<HSCategoryItems>({
    id: '',
    hs: '',
    partial: null,
    hs_version: '',
    category_id: '',
  })

  const handleDataModalChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target

    if (name === 'partial') {
      setHSItemData((prev) => ({
        ...prev,
        partial: value === 'yes' ? true : value === 'no' ? false : null,
      }))
    } else {
      setHSItemData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleAddHSCode = () => {
    setIsLoadingModal(true)
    setErrorModal(null)

    const {id, category_id, ...cleanData} = hsItemData
    const formData = {
      ...cleanData,
    }

    createHSCode(axiosPrivate, idHS, formData)
      .then((res) => {
        if (res.status === 200) {
          showSwal('Data created', undefined, 'success').then(() => {
            window.location.reload()
          })
        }
      })
      .catch((err) => {
        console.error(err)
        showSwal('Error', 'Failed to create data', 'error')
      })
      .finally(() => {
        setIsLoadingModal(false)
      })
  }
  // end of Add HS Category Item

  // Export Template
  const handleDownloadTemplate = () => {
    exportTemplate(axiosPrivate)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'hs-category-template.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((err) => {
        console.error('Failed to download template:', err)
      })
  }
  // end of Export Template

  // Import Template – open modal
  // ---- import modal state ----
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  // ----------------------------

  const handleCloseImportModal = () => {
    if (isImporting) return
    setShowImportModal(false)
    setImportFile(null)
    setImportError(null)
  }

  const handleSubmitImport = () => {
    if (!importFile) {
      setImportError('Please select a file before importing.')
      return
    }

    setIsImporting(true)
    setImportError(null)

    const formData = new FormData()
    formData.append('file', importFile)

    importDataMapping(axiosPrivate, formData)
      .then((res) => {
        if (res.status === 200) {
          setShowImportModal(false)
          showSwal('Import Successful', undefined, 'success').then(() => {
            window.location.reload()
          })
        }
      })
      .catch((resErr: any) => {
        const message = resErr?.response?.data?.message || 'Upload failed. Please check your file.'
        setImportError(message)
      })
      .finally(() => {
        setIsImporting(false)
      })
  }
  // end of Import Template

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Master Data - HS',
            path: 'master-data-hs-categories',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isDetailMode ? 'Detail' : 'Add'}
      </PageTitle>
      <div className='MasterDataHSPage'>
        <>
          <Row>
            <Col>
              <Card className='mb-5'>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>
                          {isDetailMode ? `Detail ${hsCategoryDetail.name}` : `Add HS Category`}
                        </h3>
                      </div>
                      {!isEditMode && !isAddMode && ability.can('update', subject) && (
                        <Button
                          className='btn btn-primary d-flex gap-4 align-items-center'
                          onClick={() => setIsEditMode(true)}
                        >
                          <span className='fa fa-pencil'></span>
                          Edit
                        </Button>
                      )}
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {!isLoadingData ? (
                    <Form className='form-agreement'>
                      <div className='row'>
                        <div className='col-md-4  mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            <span className={isEditMode || isAddMode ? 'required' : ''}>Name</span>
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Control
                              placeholder='Enter category name'
                              type='text'
                              name='name'
                              value={hsCategoryDetail.name}
                              onChange={handleDataChange}
                            />
                          ) : (
                            <div className='data-value'>{hsCategoryDetail.name}</div>
                          )}
                        </div>
                        <div className='col-md-4  mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            <span className={isEditMode || isAddMode ? 'required' : ''}>
                              Name Fr
                            </span>
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Control
                              placeholder='Enter category name (fr)'
                              type='text'
                              name='name_fr'
                              value={hsCategoryDetail.name_fr}
                              onChange={handleDataChange}
                            />
                          ) : (
                            <div className='data-value'>{hsCategoryDetail.name_fr}</div>
                          )}
                        </div>
                        <div className='col-md-4  mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Code
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Control
                              placeholder='Enter code'
                              type='text'
                              name='code'
                              value={hsCategoryDetail.code ?? ''}
                              onChange={handleDataChange}
                            />
                          ) : (
                            <div className='data-value'>{hsCategoryDetail.code ?? '-'}</div>
                          )}
                        </div>
                        <div className='col-md-4  mb-5'>
                          <Form.Label className='text-primary me-0' column>
                            Type
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Select
                              name='category_type'
                              value={hsCategoryDetail.category_type ?? ''}
                              onChange={handleDataChange}
                            >
                              <option value='' disabled>
                                Select type
                              </option>
                              {hsCategoryDetail.category_type && (
                                <option value=''>-- Clear selection --</option>
                              )}
                              {hsType.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <div className='data-value'>
                              {hsCategoryDetail.category_type ?? '-'}
                            </div>
                          )}
                        </div>
                        <div className='col-md-4  mb-5'>
                          <Form.Label className='text-primary me-0' column>
                            Parent Category
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Select
                              name='parent_id'
                              value={hsCategoryDetail.parent_id ?? ''}
                              onChange={handleDataChange}
                            >
                              <option value='' disabled>
                                Select parent category
                              </option>
                              {hsCategoryDetail.parent_id && (
                                <option value=''>-- Clear selection --</option>
                              )}
                              {parentCategory.map((item: any) => (
                                <option key={item.id} value={item.id}>
                                  {item.name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <div className='data-value'>
                              {hsCategoryDetail.parent_category ?? '-'}
                            </div>
                          )}
                        </div>
                        <div className='col-md-4 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Description
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <textarea
                              rows={2}
                              data-kt-element='input'
                              placeholder='Enter decsription'
                              name='desc'
                              value={hsCategoryDetail.desc ?? ''}
                              onChange={handleDataChange}
                              className='form-control mb-3'
                            ></textarea>
                          ) : (
                            <div className='data-value'>{hsCategoryDetail.desc ?? '-'}</div>
                          )}
                        </div>
                      </div>
                      <hr />
                      <div className='row'>
                        <div className='col-md-4 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            In Force Tariff
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Control
                              type='number'
                              disabled={hsCategoryDetail.category_type == 'tariff_exclusion'}
                              name='in_force_tariff'
                              placeholder='Enter category in force tariff'
                              value={hsCategoryDetail.in_force_tariff ?? ''}
                              onChange={handleDataChange}
                            />
                          ) : (
                            <div className='data-value'>
                              {hsCategoryDetail.in_force_tariff ?? '-'}
                            </div>
                          )}
                        </div>
                        <div className='col-md-4 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            In Force Tariff Type
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Select
                              disabled={hsCategoryDetail.category_type == 'tariff_exclusion'}
                              name='in_force_tariff_type'
                              placeholder='Enter category in force tariff type'
                              value={hsCategoryDetail.in_force_tariff_type ?? ''}
                              onChange={handleDataChange}
                            >
                              <option value='' selected>
                                Select in force tariff type
                              </option>
                              <option value='+'>+</option>
                              <option value='-'>-</option>
                              <option value='*'>x</option>
                              <option value='*'>/</option>
                              <option value='='>=</option>
                            </Form.Select>
                          ) : (
                            // <Form.Control
                            //   type='text'
                            //   disabled={hsCategoryDetail.category_type == 'tariff_exclusion'}
                            //   name='in_force_tariff_type'
                            //   placeholder='Enter category in force tariff type'
                            //   value={hsCategoryDetail.in_force_tariff_type ?? ''}
                            //   onChange={handleDataChange}
                            // />
                            <div className='data-value'>
                              {hsCategoryDetail.in_force_tariff_type ?? '-'}
                            </div>
                          )}
                        </div>
                      </div>
                      <hr />
                      <div className='row'>
                        <div className='col-md-4 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Partial Icon Svg
                          </Form.Label>

                          {isEditMode || isAddMode ? (
                            <>
                              {/* Changed from textarea to input file that accepts SVG only */}
                              <Form.Control
                                type='file'
                                accept='.svg'
                                name='partial_icon_svg'
                                // value={hsCategoryDetail.partial_icon_svg ?? ''}
                                onChange={handleDataChange}
                                className='form-control mb-3'
                              />
                              {/* <textarea
                                  rows={2}
                                  data-kt-element='input'
                                  placeholder='Enter description'
                                  name='partial_icon_svg'
                                  value={hsCategoryDetail.partial_icon_svg ?? ''}
                                  onChange={handleDataChange}
                                  className='form-control mb-3'
                                ></textarea> */}

                              {/* PREVIEW IMAGE */}
                              {hsCategoryDetail.partial_icon_svg ? (
                                <div className='data-value'>
                                  <div
                                    className='svg-preview'
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: hsCategoryDetail.partial_icon_svg,
                                    }}
                                  />
                                </div>
                              ) : (
                                <>-</>
                              )}
                            </>
                          ) : (
                            <div className='data-value'>
                              {hsCategoryDetail.partial_icon_svg ? (
                                <div
                                  className='svg-preview'
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: hsCategoryDetail.partial_icon_svg,
                                  }}
                                />
                              ) : (
                                <span>-</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className='col-md-4 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Full Icon Svg
                          </Form.Label>

                          {isEditMode || isAddMode ? (
                            <>
                              {/* Changed from textarea to input file that accepts SVG only */}
                              <Form.Control
                                type='file'
                                accept='.svg'
                                name='full_icon_svg'
                                // value={hsCategoryDetail.full_icon_svg ?? ''}
                                onChange={handleDataChange}
                                className='form-control mb-3'
                              />
                              {/* <textarea
                                  rows={2}
                                  data-kt-element='input'
                                  placeholder='Enter description'
                                  name='full_icon_svg'
                                  value={hsCategoryDetail.full_icon_svg ?? ''}
                                  onChange={handleDataChange}
                                  className='form-control mb-3'
                                ></textarea> */}

                              {/* PREVIEW IMAGE */}
                              {hsCategoryDetail.full_icon_svg ? (
                                <div className='data-value'>
                                  <div
                                    className='svg-preview'
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: hsCategoryDetail.full_icon_svg,
                                    }}
                                  />
                                </div>
                              ) : (
                                <>-</>
                              )}
                            </>
                          ) : (
                            <div className='data-value'>
                              {hsCategoryDetail.full_icon_svg ? (
                                <div
                                  className='svg-preview'
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: hsCategoryDetail.full_icon_svg,
                                  }}
                                />
                              ) : (
                                <span>-</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {isEditMode || isAddMode ? (
                        <div className='row'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                    </Form>
                  ) : (
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{height: '50vh'}}
                    >
                      <Spinner
                        animation='border'
                        role='status'
                        variant='primary'
                        className='me-3'
                      />
                      <div className='loading-text'>
                        Loading
                        <span className='dot'>.</span>
                        <span className='dot'>.</span>
                        <span className='dot'>.</span>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {isDetailMode && !isEditMode && (
            <Row>
              <Col>
                <Card>
                  <Card.Header className='card-header-stretch'>
                    <Card.Title className='w-100'>
                      <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                        <div className='title d-flex align-items-center'>
                          <span className='fa fa-file-alt px-2'></span>
                          <h3 className='text-gray-700'>List HS</h3>
                        </div>
                        {ability.can('create', subject) ? (
                          <div className='card-action'>
                            <Button
                              className='btn btn-primary btn-frameless'
                              onClick={() => {
                                handleDownloadTemplate()
                              }}
                            >
                              Export Template
                            </Button>
                            <Dropdown as={ButtonGroup}>
                              <Button
                                className='btn btn-primary d-flex align-items-center'
                                onClick={() => setShowAddModal(true)}
                              >
                                <i className='fa fa-plus'></i>
                                Add
                              </Button>

                              <Dropdown.Toggle
                                split
                                className='btn btn-primary dropdown-toggle-split'
                                id='dropdown-split-basic'
                              />

                              <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setShowImportModal(true)}>
                                  <i className='fa fa-file-import me-2'></i>
                                  Import Data
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        ) : (
                          ''
                        )}
                      </div>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <DataTable<DataRow>
                      data={dataTable}
                      columns={columns}
                      enablePagination={true}
                      enableSorting={true}
                      enableGlobalFilter={true}
                      globalFilterPlaceholder=''
                      defaultPageSize={10}
                      pageSizes={[5, 10, 20, 50, 100]}
                      className='my-custom-table-wrapper'
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      </div>

      {ability.can('create', subject) && (
        <ModalAddHSCode
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onSubmit={handleAddHSCode}
          onDataChange={handleDataModalChange}
          values={hsItemData}
          isLoading={isLoadingModal}
          error={errorModal}
        />
      )}

      {/* Import Modal */}
      {ability.can('create', subject) && (
        <ModalImportHSCategoriesMapping
          show={showImportModal}
          onClose={handleCloseImportModal}
          onSubmit={handleSubmitImport}
          onFileChange={setImportFile}
          isSubmitting={isImporting}
          errorMessage={importError}
        />
      )}
    </>
  )
}

export default MasterDataHSCategoryDetailPage
